import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { SetDateAndTimeView, SetDateAndTimeViewProps } from './SetDateAndTime.view';
import { TimezoneSetting } from '../../../../models/graphql';
import { useSelectedOrganization } from '../../../../hooks/useSelectedOrganization';
import { PostOnDateObject, ScheduledDateTimesType } from '../../../../models/post-editor';
import { PostEditorContext } from '../../states/PostEditorProvider';
import { PostEditorGroupDataContext } from '../../states/PostEditorGroupDataProvider';
import { dateRoundTo } from '../../../../helpers/map-timezone';
import { FormElementAction, FormElementValueType } from '../../../../models/basic-objects';
import { ScheduleHelpers } from '../Schedule.helpers';
import { addDays, isPast } from 'date-fns';
import { toDate, utcToZonedTime } from 'date-fns-tz';
import { TimezoneMap } from 'constants/timezones';
import { convertPostOnObjectToTimeZoned } from 'helpers/timeFormatter';
import { ActiveCustomizeIndexContext } from '../../states/ActiveCustomizeIndexProvider';

export type SetDateAndTimeControllerProps = {
  // extend your SetDateAndTimeViewProps
  formInstance: SetDateAndTimeViewProps['formInstance'];
  groupName: SetDateAndTimeViewProps['groupName'];
};

export type OnUpdateSetDateAndTimeFunction = (
  date: Date,
  arrayIndex: number,
  complete?: (keepNew?: boolean) => void
) => void;

export const SetDateAndTimeController = (props: SetDateAndTimeControllerProps): JSX.Element => {
  const { formInstance, groupName } = props;
  const { setValue: setFormValue, getValues: getFormValues } = formInstance;
  const [tabsState] = useContext(PostEditorGroupDataContext);
  const { postGroupIndex } = tabsState;
  const customizeIndex = useContext(ActiveCustomizeIndexContext);
  const { state, dispatch } = useContext(PostEditorContext);
  const { postGroups, loading } = state;
  const postGroup = postGroups[postGroupIndex];
  const {
    groupPostAttributes: {
      scheduling: { scheduledLocalDateTimes, scheduledTimezone }
    },
    posts,
    customizeSchedulePerProfile,
    errors
  } = postGroup;

  const { selectedWorkspace } = useSelectedOrganization();
  const wsTimezone = selectedWorkspace?.timezone ?? TimezoneSetting.UTC;

  // TODO on each render array is a new object! it triggers renders because of that, so we need to stabilize it by stringifying it
  const scheduledDatesArray =
    typeof customizeIndex === 'number'
      ? posts[customizeIndex].scheduling.scheduledLocalDateTimes
      : scheduledLocalDateTimes;
  const timeZone =
    (typeof customizeIndex === 'number' ? posts[customizeIndex].scheduling.scheduledTimezone : scheduledTimezone) ??
    wsTimezone;

  const scheduledDates = JSON.stringify(scheduledDatesArray);

  const customizedProfileId =
    customizeSchedulePerProfile && typeof customizeIndex === 'number'
      ? posts[customizeIndex].customizedProfileId
      : null;

  const dates: PostOnDateObject[] = useMemo(
    () =>
      JSON.parse(scheduledDates).map((scheduledDate: ScheduledDateTimesType) => ({
        ...ScheduleHelpers.convertIsoStringDateToPostOnDate(scheduledDate.dateTime),
        isPast: scheduledDate.isPast
      })),
    [scheduledDates]
  );
  const timeZoneName = TimezoneMap[timeZone];

  useEffect(() => {
    if (!dates) {
      return;
    }
    if (!dates.length) {
      const localTime = new Date().toISOString();
      const zonedDate = utcToZonedTime(localTime, timeZoneName);
      const currentDateZoned = toDate(zonedDate, { timeZone: timeZoneName });
      const roundedNext = dateRoundTo(currentDateZoned);
      dispatch({
        type: 'scheduledAdd',
        payload: {
          postGroupIndex,
          date: roundedNext,
          customizeIndex
        }
      });
    }
  }, [dates, postGroupIndex, timeZoneName, dispatch, getFormValues, groupName, setFormValue, customizeIndex]);

  const isTimezoneSettingGuard = (value: FormElementValueType): value is TimezoneSetting => {
    if (typeof value !== 'string') throw new Error('value is not of type string');
    return value in TimezoneSetting;
  };

  const selectAction: FormElementAction = useCallback(
    (value, complete) => {
      if (!isTimezoneSettingGuard(value)) throw new Error('Value is not a proper TimezoneSetting enum');
      complete();
      dispatch({
        type: 'changeTimeZone',
        payload: {
          scheduledTimezone: value,
          postGroupIndex,
          customizeIndex
        }
      });
    },
    [postGroupIndex, dispatch, customizeIndex]
  );

  const addNewDateHandler: VoidFunction = useCallback(() => {
    if (!dates.length) {
      return;
    }

    const lastDate = dates[dates.length - 1];
    const { zonedDate, utcDate } = convertPostOnObjectToTimeZoned(lastDate, timeZoneName);
    const nextUtc = addDays(utcDate, 1);
    const localTime = new Date().toISOString();
    const zonedNow = utcToZonedTime(localTime, timeZoneName);
    const currentDateZoned = toDate(zonedNow, { timeZone: timeZoneName });
    const roundedNext = dateRoundTo(currentDateZoned);
    const nextDay = isPast(nextUtc) ? roundedNext : addDays(zonedDate, 1);

    dispatch({
      type: 'scheduledAdd',
      payload: {
        postGroupIndex,
        date: nextDay,
        customizeIndex
      }
    });
  }, [dates, postGroupIndex, timeZoneName, dispatch, customizeIndex]);

  const removeDateHandler = useCallback(
    (scheduledIndex: number) => {
      dispatch({
        type: 'scheduledRemove',
        payload: {
          postGroupIndex,
          scheduledIndex,
          customizeIndex
        }
      });
    },
    [postGroupIndex, dispatch, customizeIndex]
  );

  const onUpdate: OnUpdateSetDateAndTimeFunction = useCallback(
    (date, arrayIndex, complete) => {
      const newDate = toDate(date, { timeZone: timeZoneName });
      complete && complete();
      dispatch({
        type: 'scheduledUpdate',
        payload: {
          postGroupIndex,
          scheduledIndex: arrayIndex,
          date: newDate,
          customizeIndex
        }
      });
    },
    [postGroupIndex, timeZoneName, dispatch, customizeIndex]
  );

  const viewProps: SetDateAndTimeViewProps = {
    ...props,
    dates,
    loading,
    timeZone,
    expandTimezoneSelector: timeZone !== wsTimezone,
    onUpdate,
    addNewDateHandler,
    selectAction,
    removeDateHandler,
    postGroupErrors: errors,
    customizedProfileId
  };
  return <SetDateAndTimeView {...viewProps} />;
};
